import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
    serviceTemplateScheduleContract,
    ServiceTemplateScheduleCreate,
    ServiceTemplateScheduleBulkActionType,
    ServiceTemplateScheduleBulkActionResponse,
} from "@common/contracts/service-template-schedule.contract";
import { ServiceTemplateSchedule } from "@common/types/serviceTemplateSchedule";
import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";
import { ServiceTemplateSchedulesSortValue } from "@common/types/views/serviceTemplateSchedulesView";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import { EngagementTeamRole } from "@common/types/taskTemplateAssigneeRole";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { extractApiErrorMessage } from "./apiError";

const serviceTemplateScheduleClient = getBackendAPIClient(
    serviceTemplateScheduleContract,
);

export const serviceTemplateScheduleQueryKeys = {
    all: () => ["service-template-schedule"] as const,
    organization: (organizationId: string) =>
        [
            ...serviceTemplateScheduleQueryKeys.all(),
            "organization",
            organizationId,
        ] as const,
    list: (
        organizationId: string,
        filters?: {
            templateId?: string;
            templateIds?: string[];
            entityUuid?: string;
            search?: string;
            sort?: ServiceTemplateSchedulesSortValue;
            periods?: ServiceTemplateRecurrencePeriod[];
            engagementTeamRoles?: EngagementTeamRole[];
            engagementTeamRoleOperator?: "any" | "every";
            engagementTeamMemberIds?: number[];
            engagementTeamMemberOperator?: "any" | "every";
            clientIds?: string[];
        },
    ) =>
        [
            ...serviceTemplateScheduleQueryKeys.organization(organizationId),
            "list",
            filters,
        ] as const,
    detail: (organizationId: string, scheduleId: string) =>
        [
            ...serviceTemplateScheduleQueryKeys.organization(organizationId),
            "detail",
            scheduleId,
        ] as const,
};

export interface UseServiceTemplateSchedulesOptions {
    organizationId: string | null | undefined;
    templateId?: string;
    /** Filters overlay: Service (close template) multi-select — distinct from the single-template `templateId` scope above. */
    templateIds?: string[];
    entityUuid?: string;
    search?: string;
    sort?: ServiceTemplateSchedulesSortValue;
    periods?: ServiceTemplateRecurrencePeriod[];
    engagementTeamRoles?: EngagementTeamRole[];
    engagementTeamRoleOperator?: "any" | "every";
    engagementTeamMemberIds?: number[];
    engagementTeamMemberOperator?: "any" | "every";
    /** Filters overlay: Client (workspace) multi-select. */
    clientIds?: string[];
    enabled?: boolean;
}

export function useServiceTemplateSchedules(
    options: UseServiceTemplateSchedulesOptions,
) {
    const {
        organizationId,
        templateId,
        templateIds,
        entityUuid,
        search,
        sort,
        periods,
        engagementTeamRoles,
        engagementTeamRoleOperator,
        engagementTeamMemberIds,
        engagementTeamMemberOperator,
        clientIds,
        enabled = true,
    } = options;

    // Normalize once so the query key matches exactly what the request sends —
    // empty arrays and operators without a selection collapse to `undefined`,
    // avoiding refetches that would produce identical backend params.
    const listQuery = {
        templateId: templateId ?? undefined,
        templateIds: templateIds?.length ? templateIds : undefined,
        entityUuid: entityUuid ?? undefined,
        search: search?.trim() ? search : undefined,
        sort: sort ?? undefined,
        periods: periods?.length ? periods : undefined,
        engagementTeamRoles: engagementTeamRoles?.length
            ? engagementTeamRoles
            : undefined,
        engagementTeamRoleOperator: engagementTeamRoles?.length
            ? engagementTeamRoleOperator
            : undefined,
        engagementTeamMemberIds: engagementTeamMemberIds?.length
            ? engagementTeamMemberIds
            : undefined,
        engagementTeamMemberOperator: engagementTeamMemberIds?.length
            ? engagementTeamMemberOperator
            : undefined,
        clientIds: clientIds?.length ? clientIds : undefined,
    };

    return useQuery({
        queryKey: organizationId
            ? serviceTemplateScheduleQueryKeys.list(organizationId, listQuery)
            : serviceTemplateScheduleQueryKeys.all(),
        queryFn: async (): Promise<ServiceTemplateSchedule[]> => {
            if (!organizationId) {
                return [];
            }

            const response =
                await serviceTemplateScheduleClient.listServiceTemplateSchedules(
                    {
                        params: { organizationId },
                        query: listQuery,
                    },
                );

            if (response.status !== 200) {
                throw new Error("Failed to fetch service template schedules");
            }

            return response.body.schedules;
        },
        enabled: !!organizationId && enabled,
    });
}

interface UseServiceTemplateScheduleOptions {
    organizationId: string | null | undefined;
    scheduleId: string | null | undefined;
    enabled?: boolean;
}

/**
 * Fetches a single schedule (a.k.a. recurrence) by id — used to open the
 * "Upcoming occurrences" drawer for the schedule a given work close belongs
 * to (see WorkActionsCell's "View occurrences" action).
 */
export function useServiceTemplateSchedule(
    options: UseServiceTemplateScheduleOptions,
) {
    const { organizationId, scheduleId, enabled = true } = options;

    return useQuery({
        queryKey:
            organizationId && scheduleId
                ? serviceTemplateScheduleQueryKeys.detail(
                      organizationId,
                      scheduleId,
                  )
                : serviceTemplateScheduleQueryKeys.all(),
        queryFn: async (): Promise<ServiceTemplateSchedule | null> => {
            if (!organizationId || !scheduleId) {
                return null;
            }

            try {
                const response =
                    await serviceTemplateScheduleClient.getServiceTemplateSchedule(
                        {
                            params: { organizationId, scheduleId },
                        },
                    );
                return response.body;
            } catch (error) {
                if (isAxiosError(error) && error.response?.status === 404) {
                    return null;
                }
                throw error;
            }
        },
        enabled: !!organizationId && !!scheduleId && enabled,
    });
}

export function useDeleteServiceTemplateScheduleMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            scheduleId,
        }: {
            organizationId: string;
            scheduleId: string;
        }) => {
            const response =
                await serviceTemplateScheduleClient.deleteServiceTemplateSchedule(
                    {
                        params: { organizationId, scheduleId },
                    },
                );

            if (response.status !== 200) {
                throw new Error("Failed to delete service template schedule");
            }
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

export function useUpdateServiceTemplateScheduleStatusMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            scheduleId,
            status,
        }: {
            organizationId: string;
            scheduleId: string;
            status: ServiceTemplateRecurrenceStatus;
        }) => {
            const response =
                await serviceTemplateScheduleClient.updateServiceTemplateSchedule(
                    {
                        params: { organizationId, scheduleId },
                        body: { status },
                    },
                );

            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to update service template schedule status",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

interface BulkServiceTemplateScheduleActionMutationVariables {
    organizationId: string;
    scheduleIds: string[];
    action: ServiceTemplateScheduleBulkActionType;
}

export function useBulkServiceTemplateScheduleActionMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            scheduleIds,
            action,
        }: BulkServiceTemplateScheduleActionMutationVariables): Promise<ServiceTemplateScheduleBulkActionResponse> => {
            const response =
                await serviceTemplateScheduleClient.bulkScheduleAction({
                    params: { organizationId },
                    body: { scheduleIds, action },
                });

            if (response.status !== 200) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to perform bulk action on schedules",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}

export function useCreateServiceTemplateScheduleMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            body,
        }: {
            organizationId: string;
            body: ServiceTemplateScheduleCreate;
        }) => {
            const response =
                await serviceTemplateScheduleClient.createServiceTemplateSchedule(
                    {
                        params: { organizationId },
                        body,
                    },
                );

            if (response.status !== 201) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        "Failed to create service template schedule",
                    ),
                );
            }

            return response.body;
        },
        onSuccess: (_data, { organizationId }) => {
            void queryClient.invalidateQueries({
                queryKey:
                    serviceTemplateScheduleQueryKeys.organization(
                        organizationId,
                    ),
            });
        },
    });
}
